import { useCallback, useEffect, useState } from 'react';
import type { BookingCapacity } from '@bbbk/schema';
import { api, ApiError } from '../../lib/api';
import { useToast, writeWithToast } from '../common';
import { useMutationTick } from '../../lib/mutationBus';
import { revalidateBooking } from './revalidateBooking';

// THE ONE ACTION WITH TWO DOORS (accept-or-decline AC4, AC5; decision 240).
// The Needs attention row and the strip on the booking both decide through
// here — one POST each, one toast, one revalidation — so the two doors cannot
// grow two behaviours. The write to /api/bookings/* signals the mutation bus,
// which is what refreshes the Daily's lists; nothing here reloads them by hand.

export type Verdict = 'accept' | 'decline';

export function decisionError(e: unknown, verdict: Verdict): string {
  if (e instanceof ApiError) {
    // The size gate's own sentence — *Bracken has no size.* — is the prompt
    // (AC17): she sizes the dog, then accepts.
    if (e.code === 'validation_error' && e.message) return `Unable to accept — ${e.message} Set the size on the household first.`;
    if (e.code === 'already_decided') return 'This request was already decided — the page has been refreshed.';
    if (e.code === 'request_lapsed') return 'Those dates have passed — this request can only be declined.';
  }
  return verdict === 'accept' ? 'Could not accept the request.' : 'Could not decline the request.';
}

export function useDecide(onDone?: (id: string, verdict: Verdict) => void) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [verdictInFlight, setVerdict] = useState<Verdict | null>(null);
  const decide = useCallback((id: string, verdict: Verdict) => {
    if (busy) return; // a decision in flight — a second tap is ignored (AC16)
    setBusy(id);
    setVerdict(verdict);
    void writeWithToast(toast, {
      // THE SERVER'S YES IS THE MOMENT (Freddi, 2026-10-11: "seamless"): the card flips
      // as soon as the decision is written; the re-reads follow off the critical path.
      write: async () => {
        await api(`/bookings/${id}/${verdict}`, { method: 'POST' });
      },
      success: verdict === 'accept' ? 'Request accepted — deposit asked' : 'Request declined',
      error: (e) => decisionError(e, verdict),
      onSaved: () => { setBusy(null); setVerdict(null); onDone?.(id, verdict); void revalidateBooking(id); },
      onError: (e) => {
        setBusy(null);
        setVerdict(null);
        // Already decided elsewhere: re-read, so the screen stops offering a choice that is gone.
        if (e instanceof ApiError && e.code === 'already_decided') void revalidateBooking(id).then(() => onDone?.(id, verdict));
      },
    });
  }, [busy, toast, onDone]);
  return { busy, verdict: verdictInFlight, accept: (id: string) => decide(id, 'accept'), decline: (id: string) => decide(id, 'decline') };
}

/**
 * The fact for each request id given (AC3) — re-read whenever a booking is
 * written, so accepting one of two overlapping requests moves the other's
 * figures at once; and only while `enabled`, so the Daily does not spend a
 * read per open request on a panel nobody has opened (PR #251 review).
 */
export function useCapacities(ids: readonly string[], enabled = true): Record<string, BookingCapacity | undefined> {
  const [byId, setById] = useState<Record<string, BookingCapacity | undefined>>({});
  const key = ids.join(',');
  const tick = useMutationTick(['bookings']);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    for (const id of ids) {
      void api<BookingCapacity>(`/bookings/${id}/capacity`, { cache: 'reload' })
        .then((r) => { if (live) setById((m) => ({ ...m, [id]: r.data })); })
        .catch(() => undefined); // no fact is shown, the decision still is
    }
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, tick, enabled]);
  return byId;
}
